import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import api from '../../services/api';

const getRoleLabel = (role) => {
  const labels = {
    admin: 'Admin',
    operator: 'Operator',
    viewer: 'Viewer',
    warehouse: 'Magazijn'
  };
  return labels[role] || role;
};

const getRoleBadgeColor = (role) => {
  const colors = {
    admin: 'dark',
    operator: 'light',
    viewer: 'secondary',
    warehouse: 'info'
  };
  return colors[role] || 'secondary';
};

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const openPasswordModal = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordError('Vul alle velden in');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Nieuw wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Wachtwoorden komen niet overeen');
      return;
    }

    try {
      setPasswordSaving(true);
      await api.put('/auth/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordSuccess('Wachtwoord succesvol gewijzigd');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowPasswordModal(false), 1500);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Fout bij wijzigen wachtwoord');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <nav className="navbar navbar-dark fixed-top px-3">
      <a className="navbar-brand d-flex align-items-center" href="/">
        <img
          src="/logo.svg"
          alt="Tafelzeil Online"
          height="28"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
      </a>
      <div className="d-flex align-items-center">
        <span className="text-white-50 me-3 small">
          <i className="bi bi-person-circle me-1"></i>
          {user?.name || 'Gebruiker'}
          {user?.role && (
            <span className={`badge ms-2 bg-${getRoleBadgeColor(user.role)}`}>
              {getRoleLabel(user.role)}
            </span>
          )}
        </span>
        <button
          onClick={openPasswordModal}
          className="btn btn-sm btn-outline-light me-2"
          title="Wachtwoord wijzigen"
        >
          <i className="bi bi-key me-1"></i>
          Wachtwoord
        </button>
        <button onClick={handleLogout} className="btn btn-sm btn-outline-light">
          <i className="bi bi-box-arrow-right me-1"></i>
          Uitloggen
        </button>
      </div>

      {showPasswordModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Wachtwoord wijzigen</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPasswordModal(false)}
                ></button>
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <div className="modal-body">
                  {passwordError && (
                    <div className="alert alert-danger">{passwordError}</div>
                  )}
                  {passwordSuccess && (
                    <div className="alert alert-success">{passwordSuccess}</div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Huidig wachtwoord *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Nieuw wachtwoord *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      placeholder="Minimaal 6 tekens"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Bevestig nieuw wachtwoord *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Annuleren
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
                    {passwordSaving ? 'Bezig...' : 'Wijzigen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
